import { ColumnProps, styled } from "@monolith-forensics/monolith-ui";

export type DetailsDisplayOrientation = "horizontal" | "vertical";

const StyledContainer = styled.div<{
  $orientation?: DetailsDisplayOrientation;
}>`
  display: grid;
  gap: 10px;
  grid-template-columns: ${({ $orientation }) =>
    $orientation === "vertical" ? "1fr" : "repeat(4, 1fr)"};
  width: 100%;
`;

export const DetailItem = styled.div<{
  $orientation?: DetailsDisplayOrientation;
}>`
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ $orientation }) =>
    $orientation === "vertical" ? "row" : "column"};
  gap: ${({ $orientation }) => ($orientation === "vertical" ? "5px" : "5px")};
  width: ${({ $orientation }) =>
    $orientation === "vertical" ? "100%" : "auto"};
  min-height: ${({ $orientation }) =>
    $orientation === "vertical" ? "100%" : "50px"};
  padding-bottom: ${({ $orientation }) =>
    $orientation === "vertical" ? "0px" : "10px"};
`;

export const DetailLabel = styled.div<{
  $orientation?: DetailsDisplayOrientation;
}>`
  font-weight: bold;
  user-select: none;
  margin-right: ${({ $orientation }) =>
    $orientation === "vertical" ? "0px" : "8px"};
  width: ${({ $orientation }) =>
    $orientation === "vertical" ? "160px" : "auto"};
  min-width: ${({ $orientation }) =>
    $orientation === "vertical" ? "160px" : "auto"};
  color: ${({ theme }) =>
    theme.name === "DARK"
      ? theme.palette.text.secondary
      : theme.palette.text.primary};
`;

export const DetailValue = styled.div<{
  $orientation?: DetailsDisplayOrientation;
}>`
  white-space: pre-wrap;
  max-height: ${({ $orientation }) =>
    $orientation === "vertical" ? "none" : "150px"};
  overflow-y: auto;
  width: ${({ $orientation }) =>
    $orientation === "vertical" ? "100%" : "auto"};
  padding: 2px 0px;
`;

export type DetailsDisplayProps<T = any> = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  orientation?: DetailsDisplayOrientation;
  columnDefinitions: ColumnProps[];
  data: T;
  dataFieldIgnoreList?: string[];
  hideEmptyFields?: boolean;
  onUpdate?: ({ rowData, updateData }: { rowData: T; updateData: T }) => void;
};

export function DetailsDisplay<T extends Record<string, any> = any>({
  children,
  style,
  orientation = "horizontal",
  columnDefinitions,
  data,
  dataFieldIgnoreList = [],
  hideEmptyFields = false,
  onUpdate,
}: DetailsDisplayProps<T>) {
  const handleUpdate = ({
    rowData,
    updatedData,
  }: {
    rowData: T;
    updatedData: T;
  }) => {
    onUpdate?.({ rowData, updateData: updatedData });
  };

  return (
    <StyledContainer $orientation={orientation} style={style}>
      {columnDefinitions
        .filter((column) => !dataFieldIgnoreList.includes(column.dataField))
        .map((column) => ({
          column,
          renderedValue: column.render
            ? column.render({ rowData: data, onRowUpdated: handleUpdate })
            : data[column.dataField],
        }))
        .filter(({ renderedValue }) => {
          if (!hideEmptyFields) return true;
          return (
            renderedValue !== null &&
            renderedValue !== undefined &&
            renderedValue !== ""
          );
        })
        .map(({ column, renderedValue }) => (
          <DetailItem
            key={column.dataField}
            $orientation={orientation}
            data-orientation={orientation}
          >
            <DetailLabel $orientation={orientation}>
              {column.caption || column.dataField}
            </DetailLabel>
            <DetailValue $orientation={orientation}>
              {renderedValue}
            </DetailValue>
          </DetailItem>
        ))}
      {children}
    </StyledContainer>
  );
}
